/**
 * Las ofertas: what to put on sale, how much off, for how long; and what the till charges while one runs.
 *
 * The owner, 2026-11-03: «i want a tool that suggests to me products that haven't been moving so well
 * and i want the tool to recommend me which products to put on sale and for how long, basically a
 * promotion generator».
 *
 * Two reasons to put something on sale, each with its own measure, its own discount and its own days:
 *
 * - «Se vence» (`vence`): a batch that will not sell before its date at the pace the product sells.
 *   A sale draws the oldest batch first, so by each batch's date what has to have gone is everything
 *   that expires by then; what the pace leaves of it is lost whole on that day. The sooner, the more
 *   off: 40% at three days or fewer, 30% within a week, 20% within two, 15% further. The offer runs
 *   until that date. What it charges stays at what the goods cost, unless that leaves less than 5%
 *   off: then the discount stands under the cost, said so, since what expires unsold brings nothing.
 *
 * - «Se mueve poco» (`lento`): more than eight weeks of stock at its pace, or no sale at all in four
 *   weeks, of something that did not just come in (three weeks: what arrived has not had the chance)
 *   and was not on an offer in the last thirty days (one after another teaches the customers to
 *   wait for the next). 10% off past eight weeks, 15% past twelve, 20% past twenty-six, 25% for what
 *   did not sell at all; for two weeks, three for what did not sell. Never under what it cost: money
 *   lost on what keeps is a decision for the owner, typed in the sheet, and not a suggestion; and what
 *   has less than 5% of room above its cost is not suggested.
 *
 * The pace is the one «Por comprar» measures (`reponer.ts`): four weeks of sales, less what came back,
 * over the days the product has been in the shop, a week at least. What sold in an offer is not in it.
 *
 * On the shop's own list only (`esPredeterminada`): the wholesale customer has their price already.
 * Each presentation with a price there is offered the same percentage off, rounded down to the fifty
 * centavos, and up to them when that would go under the cost.
 *
 * What expires first, then what is worth most sitting there.
 *
 * Pure, so it can be tested; `Ofertas.tsx` hands it the tables.
 */

import {
  centavos,
  costOf,
  costoDeUnidadVenta,
  diasEntre,
  formatMoney,
  lineTotal,
  margenBp,
  saleToBase,
  subQty,
  sumarDias,
  unitCostFromTotal,
  ZERO_COST,
  ZERO_QTY,
  type Centavos,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import {
  DIAS_MAXIMOS_OFERTA,
  precioId,
  ultimoDiaDeOferta,
  type Compra,
  type Existencia,
  type Lote,
  type Oferta,
  type Precio,
  type Producto,
  type Salida,
  type UnidadVenta,
  type Venta,
  type VentaLinea,
} from '@abastece/core';

/**
 * A presentation's price on a list (`catalogo.ts`'s `precioDe`, here so that this file needs nothing
 * but the books' types: the receipt reads it).
 */
function precioDe(precios: readonly { id: string; precio: Centavos }[], unidadVentaId: string, listaId: string): Centavos | null {
  const id = precioId(unidadVentaId, listaId);
  return precios.find((p) => p.id === id)?.precio ?? null;
}

/** How far back the pace is measured: the four weeks of «Por comprar». */
export const VENTANA_OFERTAS = 28;
/** The fewest days a pace is spread over: a product a day old has not shown one. */
const DIAS_MINIMOS = 7;
/** More weeks of stock than this at its pace is moving slowly. */
export const SEMANAS_LENTO = 8;
/** What came in on a purchase within these days has not had the chance to sell. */
export const DIAS_RECIEN_LLEGADO = 21;
/** After an offer on something slow, this many days before another is suggested for it. */
export const DIAS_ENTRE_OFERTAS = 30;
/** The least a suggestion takes off: less than this nobody notices. */
export const DESCUENTO_MINIMO_BP = 500;

// ---------------------------------------------------------------------------
// Lo que corre
// ---------------------------------------------------------------------------

/** Where an offer is on a day: still to start, running, or done with (ended, or its days gone by). */
export type EstadoOferta = 'programada' | 'corre' | 'terminada';

export function estadoDeOferta(o: Pick<Oferta, 'desde' | 'hasta' | 'terminadaEl'>, dia: string): EstadoOferta {
  const ultimo = ultimoDiaDeOferta(o);
  if (ultimo < dia || ultimo < o.desde) return 'terminada';
  return o.desde > dia ? 'programada' : 'corre';
}

/** The offer a product has running on a list on a day. One at a time on a list: the books see to it. */
export function ofertaQueCorre(ofertas: readonly Oferta[], productoId: string, listaId: string, dia: string): Oferta | undefined {
  return ofertas.find((o) => o.productoId === productoId && o.listaId === listaId && estadoDeOferta(o, dia) === 'corre');
}

/** «So many for fewer» (2026-11-03): `cantidad` of it for the price of `paga`. */
export interface Lleva {
  cantidad: number;
  paga: number;
}

/** What the till charges for a presentation: the price, and the offer it comes from with the list's own. */
export interface PrecioEnCaja {
  precio: Centavos;
  ofertaId?: string;
  /** The list's price without the offer, when the list has one. */
  precioRegular?: Centavos;
  /** So many for fewer: the price is the list's, and of every `cantidad` whole ones `cantidad − paga` are free. */
  lleva?: Lleva;
}

/** «2×1», «3×2»: so many for fewer, as the shops write it. */
export const nombreDeLleva = (l: Lleva) => `${l.cantidad}×${l.paga}`;

/**
 * What the till charges for a presentation on a list on a day: an offer's price while one runs that
 * names it, the list's own price beside it; otherwise the list's. An offer dearer than the list's
 * price, the price lowered since it was made, is not charged: the list's is. So many for fewer is
 * charged at the list's price with its `lleva`, and not at all without one: nothing to charge the
 * ones that are paid. Null with neither.
 */
export function precioEnCaja(
  precios: readonly { id: string; precio: Centavos }[],
  ofertas: readonly Oferta[],
  unidad: Pick<UnidadVenta, 'id' | 'productoId'>,
  listaId: string,
  dia: string,
): PrecioEnCaja | null {
  const regular = precioDe(precios, unidad.id, listaId);
  const o = ofertaQueCorre(ofertas, unidad.productoId, listaId, dia);
  const suyo = o?.precios.find((x) => x.unidadVentaId === unidad.id);
  if (o && suyo) {
    if ('lleva' in suyo) {
      if (regular !== null) return { precio: regular, ofertaId: o.id, precioRegular: regular, lleva: { cantidad: suyo.lleva, paga: suyo.paga } };
    } else if (regular === null || suyo.precio < regular) {
      return { precio: suyo.precio, ofertaId: o.id, ...(regular !== null ? { precioRegular: regular } : {}) };
    }
  }
  return regular === null ? null : { precio: regular };
}

/**
 * Of so many at a «lleva N, paga M», the ones not charged: `N − M` of every `N` whole ones. Counted in
 * whole ones of what is sold: half a pound is not a pound towards the next free one.
 */
export function gratisDe(cantidad: Milli, lleva: Lleva): Milli {
  const grupos = Math.floor(Math.floor(cantidad / 1000) / lleva.cantidad);
  return (grupos * (lleva.cantidad - lleva.paga) * 1000) as Milli;
}

/** A line of the sale being rung up, as these read it. */
type LineaEnCaja = Pick<VentaLinea, 'cantidad' | 'precioLista' | 'precioUnitario' | 'ofertaId' | 'precioRegular'> & { lleva?: Lleva | undefined };

/** Of a line being rung up, the ones its «lleva» gives: none at a price typed by hand, which is a decision of its own. */
export const gratisEnCaja = (l: LineaEnCaja): Milli => (l.lleva && l.precioUnitario === l.precioLista ? gratisDe(l.cantidad, l.lleva) : (0 as Milli));

/** What a line being rung up comes to: what is paid of it at its price. */
export const subtotalEnCaja = (l: LineaEnCaja): Centavos => lineTotal(l.precioUnitario, subQty(l.cantidad, gratisEnCaja(l)));

/** What a line being rung up saves: the list's own price on all of it, less what it comes to. */
export function ahorroEnCaja(l: LineaEnCaja): Centavos {
  if (!l.ofertaId || l.precioRegular === undefined) return 0 as Centavos;
  return Math.max(0, lineTotal(l.precioRegular, l.cantidad) - subtotalEnCaja(l)) as Centavos;
}

/**
 * The «lleva N, paga N − 1» nearest to a share off (`descuentoBp`): one free of every N is 1/N off,
 * so 50% is 2×1 and 25% is 4×3; from two to ten. Unless it is for an expiring batch, never under what
 * N cost: N grows until the N − 1 paid at the list's price cover them, and with none that does, null.
 */
export function llevaParecido(descuentoBp: number, regular: Centavos, costo: Centavos, bajoCostoPermitido: boolean): Lleva | null {
  for (let n = Math.min(10, Math.max(2, Math.round(10_000 / Math.max(descuentoBp, 1)))); n <= 10; n++) {
    if (bajoCostoPermitido || costo <= 0 || (n - 1) * regular >= n * costo) return { cantidad: n, paga: n - 1 };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Lo que se sugiere
// ---------------------------------------------------------------------------

export type MotivoSugerido = 'vence' | 'lento';

/** A presentation as the suggestion has it: its price on the list, what one costs, and the offer's. */
export interface PrecioSugerido {
  unidadVentaId: string;
  nombre: string;
  factor: Milli;
  regular: Centavos;
  /** What one costs, at what the product is carried at; zero when nothing says. */
  costo: Centavos;
  oferta: Centavos;
}

export interface SugerenciaOferta {
  productoId: string;
  nombre: string;
  motivo: MotivoSugerido;
  /** What there is of it everywhere, in its base unit. */
  cantidad: Milli;
  /** What that is carried at. */
  valor: Centavos;
  /** Its pace: base units a day, out of offers. */
  porDia: number;
  /** The weeks what there is lasts at that pace; null when it did not sell in the four weeks. */
  semanas: number | null;
  /** The last day of the four weeks it sold; null when it did not. */
  ultimaVenta: string | null;
  /**
   * Of an expiring product: the first date by which more expires than the pace sells, the whole days
   * until it, and how much of it is left over then, in base units.
   */
  vence: { fecha: string; dias: number; sobra: Milli } | null;
  /** What is at stake: what is left over by that date, or everything there is, at what it cost. */
  enJuego: Centavos;
  /** What the suggestion takes off, in basis points of the list's price. */
  descuentoBp: number;
  precios: PrecioSugerido[];
  desde: string;
  hasta: string;
  /** Some price went up to the cost, short of the discount its reason asks. */
  alCosto: boolean;
  /** Some price stands under its cost: an expiring batch, what is left of it otherwise lost. */
  bajoCosto: boolean;
}

export interface DatosOfertas {
  productos: readonly Producto[];
  unidades: readonly UnidadVenta[];
  precios: readonly Precio[];
  /** The shop's own list (`esPredeterminada`): the one offers go on. */
  listaId: string;
  existencias: readonly Existencia[];
  lotes: readonly Lote[];
  /** At least the four weeks' sales; voided ones and older ones are left out here. */
  ventas: readonly Venta[];
  /** For when each product last came in, and what it cost when nothing in stock says. */
  compras: readonly Compra[];
  ofertas: readonly Oferta[];
  hoy: string;
  /** How far ahead a batch's date counts (`vencimientoAlertaDias`). */
  alertaDias: number;
  /** Products put aside on this device. */
  noSugeridos?: ReadonlySet<string>;
}

/** Down to the fifty centavos: a price somebody can charge in coins. */
const abajoCincuenta = (c: number): Centavos => centavos(Math.floor(c / 50) * 50);
/** Up to them. */
const arribaCincuenta = (c: number): Centavos => centavos(Math.ceil(c / 50) * 50);

/** What comes off an expiring batch, by the whole days until its date. */
export function descuentoPorVencer(dias: number): number {
  return dias <= 3 ? 4_000 : dias <= 7 ? 3_000 : dias <= 14 ? 2_000 : 1_500;
}

/** What comes off a slow product, by the weeks it lasts; null weeks, it did not sell at all. */
export function descuentoPorLento(semanas: number | null): number {
  return semanas === null ? 2_500 : semanas > 26 ? 2_000 : semanas > 12 ? 1_500 : 1_000;
}

export function sugerirOfertas(d: DatosOfertas): SugerenciaOferta[] {
  const desde = sumarDias(d.hoy, -(VENTANA_OFERTAS - 1));
  const ventas = d.ventas.filter((v) => !v.anulada && v.fecha >= desde && v.fecha <= d.hoy);
  const primera = ventas.reduce<string | null>((a, v) => (a === null || v.fecha < a ? v.fecha : a), null);
  const diasTienda = primera === null ? VENTANA_OFERTAS : Math.max(DIAS_MINIMOS, diasEntre(primera, d.hoy) + 1);

  // What left, in base units, out of offers; and the last day each sold.
  const salio = new Map<string, number>();
  const ultimaVenta = new Map<string, string>();
  for (const v of ventas) {
    for (const l of v.lineas) {
      if (!l.productoId) continue;
      const neta = subQty(l.cantidad, l.devuelta ?? ZERO_QTY);
      if (neta <= 0) continue;
      if (v.fecha > (ultimaVenta.get(l.productoId) ?? '')) ultimaVenta.set(l.productoId, v.fecha);
      if (!l.ofertaId) salio.set(l.productoId, (salio.get(l.productoId) ?? 0) + saleToBase(neta, l.factor));
    }
  }

  // When each product first and last came in on a purchase, and what the last one cost a base unit.
  const primeraCompra = new Map<string, string>();
  const ultimaCompra = new Map<string, { fecha: string; costo: UnitCost }>();
  for (const c of d.compras) {
    if (c.anulada) continue;
    for (const l of c.lineas) {
      if (l.cantidadBase <= 0) continue;
      if (c.fecha < (primeraCompra.get(l.productoId) ?? '9999')) primeraCompra.set(l.productoId, c.fecha);
      const antes = ultimaCompra.get(l.productoId);
      if (!antes || c.fecha >= antes.fecha) ultimaCompra.set(l.productoId, { fecha: c.fecha, costo: unitCostFromTotal(l.costoCordobas, l.cantidadBase) });
    }
  }

  // What there is of each, what it is carried at, and what a base unit costs: the counter's average,
  // or the average where the most of it is, or the last purchase's (`costoConocido`).
  const hay = new Map<string, { cantidad: number; valor: number; tienda: UnitCost; mayor: { cantidad: number; costo: UnitCost } | null }>();
  for (const e of d.existencias) {
    const x = hay.get(e.productoId) ?? { cantidad: 0, valor: 0, tienda: ZERO_COST, mayor: null };
    if (e.cantidad > 0) {
      x.cantidad += e.cantidad;
      x.valor += costOf(e.costoPromedio, e.cantidad);
    }
    if (e.costoPromedio > 0) {
      if (e.ubicacion === 'tienda') x.tienda = e.costoPromedio;
      else if (!x.mayor || e.cantidad > x.mayor.cantidad) x.mayor = { cantidad: e.cantidad, costo: e.costoPromedio };
    }
    hay.set(e.productoId, x);
  }
  const costoBase = (productoId: string): UnitCost => {
    const x = hay.get(productoId);
    if (x && x.tienda > 0) return x.tienda;
    if (x?.mayor) return x.mayor.costo;
    return ultimaCompra.get(productoId)?.costo ?? ZERO_COST;
  };

  // The batches still to expire within the window of the alert, each product's by date.
  const limite = sumarDias(d.hoy, d.alertaDias);
  const lotesDe = new Map<string, Lote[]>();
  for (const l of d.lotes) {
    if (l.cantidadDisponible <= 0 || l.vencimiento < d.hoy) continue;
    const suyos = lotesDe.get(l.productoId) ?? [];
    suyos.push(l);
    lotesDe.set(l.productoId, suyos);
  }

  // The offers each product has had on the list: one running or to come leaves it be; one that ended
  // within the days between offers holds back another for being slow.
  const enOferta = new Set<string>();
  const ofertaReciente = new Set<string>();
  const desdeLaUltima = sumarDias(d.hoy, -DIAS_ENTRE_OFERTAS);
  for (const o of d.ofertas) {
    if (o.listaId !== d.listaId) continue;
    const estado = estadoDeOferta(o, d.hoy);
    if (estado !== 'terminada') enOferta.add(o.productoId);
    else if (ultimoDiaDeOferta(o) >= desdeLaUltima && ultimoDiaDeOferta(o) >= o.desde) ofertaReciente.add(o.productoId);
  }

  const unidadesDe = new Map<string, UnidadVenta[]>();
  for (const u of d.unidades) {
    const suyas = unidadesDe.get(u.productoId) ?? [];
    suyas.push(u);
    unidadesDe.set(u.productoId, suyas);
  }

  const lista: SugerenciaOferta[] = [];
  for (const p of d.productos) {
    if (!p.activo || enOferta.has(p.id) || d.noSugeridos?.has(p.id)) continue;
    const existencia = hay.get(p.id);
    if (!existencia || existencia.cantidad <= 0) continue;
    const presentaciones = (unidadesDe.get(p.id) ?? [])
      .map((u) => ({ u, regular: precioDe(d.precios, u.id, d.listaId) }))
      .filter((x): x is { u: UnidadVenta; regular: Centavos } => x.regular !== null && x.regular > 0)
      .sort((a, b) => a.u.factor - b.u.factor || (a.u.id < b.u.id ? -1 : 1));
    if (presentaciones.length === 0) continue;

    // Its pace, over the days it has been here: from its first purchase when that came after the
    // shop's first sale of the four weeks, or its own first sale if earlier still.
    const llego = primeraCompra.get(p.id);
    const suyos = llego !== undefined && primera !== null && llego > primera ? Math.max(DIAS_MINIMOS, diasEntre(llego, d.hoy) + 1) : diasTienda;
    const porDia = (salio.get(p.id) ?? 0) / Math.min(diasTienda, suyos);
    const semanas = porDia > 0 ? existencia.cantidad / porDia / 7 : null;
    const unidadCosto = costoBase(p.id);

    // Expiring: by each batch's date, what expires by then less what the pace sells by then.
    let vence: SugerenciaOferta['vence'] = null;
    let acumulado = 0;
    for (const l of [...(lotesDe.get(p.id) ?? [])].sort((a, b) => a.vencimiento.localeCompare(b.vencimiento) || (a.id < b.id ? -1 : 1))) {
      acumulado += l.cantidadDisponible;
      if (l.vencimiento > limite) break;
      const dias = diasEntre(d.hoy, l.vencimiento);
      // Today and its date included: a batch is sold until the end of the day it expires.
      const sobra = Math.round(acumulado - porDia * (dias + 1));
      if (sobra > 0) {
        vence = { fecha: l.vencimiento, dias, sobra: Math.min(sobra, existencia.cantidad) as Milli };
        break;
      }
    }

    let motivo: MotivoSugerido;
    let bp: number;
    let hasta: string;
    if (vence) {
      motivo = 'vence';
      bp = descuentoPorVencer(vence.dias);
      hasta = vence.dias + 1 > DIAS_MAXIMOS_OFERTA ? sumarDias(d.hoy, DIAS_MAXIMOS_OFERTA - 1) : vence.fecha;
    } else {
      const lento = semanas === null || semanas > SEMANAS_LENTO;
      const recien = (ultimaCompra.get(p.id)?.fecha ?? '') > sumarDias(d.hoy, -DIAS_RECIEN_LLEGADO);
      if (!lento || recien || ofertaReciente.has(p.id)) continue;
      motivo = 'lento';
      bp = descuentoPorLento(semanas);
      hasta = sumarDias(d.hoy, (semanas === null ? 21 : 14) - 1);
    }

    let alCosto = false;
    let bajoCosto = false;
    const precios: PrecioSugerido[] = [];
    for (const { u, regular } of presentaciones) {
      const costo = unidadCosto > 0 ? costoDeUnidadVenta(unidadCosto, u.factor) : (0 as Centavos);
      const conDescuento = abajoCincuenta((regular * (10_000 - bp)) / 10_000);
      const piso = costo > 0 ? arribaCincuenta(costo) : (0 as Centavos);
      let oferta = conDescuento;
      if (conDescuento < piso) {
        // At the cost, if that still takes enough off; an expiring batch keeps its discount otherwise.
        if ((regular - piso) * 10_000 >= DESCUENTO_MINIMO_BP * regular) {
          oferta = piso;
          alCosto = true;
        } else if (motivo === 'vence') {
          bajoCosto = true;
        } else {
          continue;
        }
      }
      if (oferta <= 0 || oferta >= regular) continue;
      precios.push({ unidadVentaId: u.id, nombre: u.nombre, factor: u.factor, regular, costo, oferta });
    }
    if (precios.length === 0) continue;

    lista.push({
      productoId: p.id,
      nombre: p.nombre,
      motivo,
      cantidad: existencia.cantidad as Milli,
      valor: centavos(Math.round(existencia.valor)),
      porDia,
      semanas,
      ultimaVenta: ultimaVenta.get(p.id) ?? null,
      vence,
      enJuego: vence ? costOf(unidadCosto, vence.sobra) : centavos(Math.round(existencia.valor)),
      descuentoBp: bp,
      precios,
      desde: d.hoy,
      hasta,
      alCosto,
      bajoCosto,
    });
  }

  // What expires first, sooner first; then what is worth most sitting there. By name among equals.
  return lista.sort(
    (a, b) =>
      (a.vence ? 0 : 1) - (b.vence ? 0 : 1) ||
      (a.vence && b.vence ? a.vence.fecha.localeCompare(b.vence.fecha) : 0) ||
      b.enJuego - a.enJuego ||
      a.nombre.localeCompare(b.nombre, 'es'),
  );
}

/** The share a price takes off another, in basis points: 2,000 is 20% off. */
export function descuentoDe(oferta: Centavos, regular: Centavos): number {
  return regular > 0 ? Math.round(((regular - oferta) * 10_000) / regular) : 0;
}

/**
 * How much a price takes off another, as the screen's badge says it beside the two (`PreciosDeOferta`):
 * «−30%»; under one per cent, which rounds to nothing, the money, «−C$ 0.10».
 */
export function menosDe(oferta: Centavos, regular: Centavos): string {
  const porCiento = Math.round(descuentoDe(oferta, regular) / 100);
  return porCiento >= 1 ? `−${porCiento}%` : `−${formatMoney((regular - oferta) as Centavos)}`;
}

// ---------------------------------------------------------------------------
// Antes y en oferta
// ---------------------------------------------------------------------------

/**
 * A presentation at an offer, before it and on it, for the same quantity (the owner, 2026-11-03: «the
 * cards outside of the form should also show a clear before and after regarding the prices/margins»):
 * one at a lower price, or `cuantas` at so many for fewer. What the list's price charges for them
 * and what the offer charges; and, with what one costs, what they cost, the margin of each (basis
 * points, `margenBp`) and what each leaves, under nothing a loss.
 */
export interface Comparacion {
  /** One, or so many for fewer's `cantidad`: the price row says «Precio de 3». */
  cuantas: number;
  /** What the list charges for them; null with no price on it. */
  antes: Centavos | null;
  /** What the offer charges for them; null for so many for fewer with no list's price to charge the ones paid. */
  ahora: Centavos | null;
  /** The offer charges less than the list: an offer the till charges. */
  baja: boolean;
  /** What they cost; null when nobody knows, or it is not for whoever looks. */
  costo: Centavos | null;
  margenAntes: number | null;
  margenAhora: number | null;
  gananciaAntes: Centavos | null;
  gananciaAhora: Centavos | null;
}

export function comparacionDeOferta(regular: Centavos | null, oferta: { precio: Centavos } | { lleva: Lleva }, costoUno: Centavos | null): Comparacion {
  const cuantas = 'lleva' in oferta ? oferta.lleva.cantidad : 1;
  const antes = regular === null ? null : ((regular * cuantas) as Centavos);
  const ahora = 'lleva' in oferta ? (regular === null ? null : ((regular * oferta.lleva.paga) as Centavos)) : oferta.precio;
  const costo = costoUno !== null && costoUno > 0 ? ((costoUno * cuantas) as Centavos) : null;
  const margen = (precio: Centavos | null) => (precio !== null && precio > 0 && costo !== null ? margenBp(precio, costo) : null);
  const ganancia = (precio: Centavos | null) => (precio !== null && costo !== null ? ((precio - costo) as Centavos) : null);
  return {
    cuantas,
    antes,
    ahora,
    baja: antes !== null && ahora !== null && ahora < antes,
    costo,
    margenAntes: margen(antes),
    margenAhora: margen(ahora),
    gananciaAntes: ganancia(antes),
    gananciaAhora: ganancia(ahora),
  };
}

// ---------------------------------------------------------------------------
// Los botones de la hoja
// ---------------------------------------------------------------------------

/** What a share off writes in a presentation's box on the sheet: that much off its price, down to the fifty centavos. */
export const precioConDescuento = (regular: Centavos, pct: number): Centavos => abajoCincuenta((regular * (100 - pct)) / 100);

/** A button of the sheet's row of shares off: what it says it takes off (none when that differs by presentation), and whether it is the suggestion. */
export interface BotonDescuento {
  clave: string;
  pct: number | null;
  sugerido: boolean;
}

/**
 * The sheet's row of shares off, with the suggestion among them (the owner, 2026-11-03: «it should
 * clearly show suggested discount as the default highlighted option»). When what the suggestion puts
 * in every box is what one of the buttons writes (`precioConDescuento`), that button is it
 * (`sugeridoEsBoton`). When not (a price went up to its cost, a presentation was left out, or its
 * share off is none of the buttons), a button of its own, `clave` «sugerido», saying how much it
 * takes off when that is the same for every price it names, in its place among the others, and
 * first, saying nothing of it, when it is not: a «−25%» writing another price than the
 * suggestion's would say what it does not do. While the presentations are not read yet, the
 * buttons alone.
 */
export function botonesDeDescuento(
  descuentos: readonly number[],
  presentaciones: readonly { unidadVentaId: string; regular: Centavos }[],
  sugerencia: Pick<SugerenciaOferta, 'descuentoBp' | 'precios'> | undefined,
): { botones: BotonDescuento[]; sugeridoEsBoton: boolean } {
  const sugeridos = new Map((sugerencia?.precios ?? []).map((p) => [p.unidadVentaId, p.oferta]));
  const pct = sugerencia ? Math.round(sugerencia.descuentoBp / 100) : null;
  const sugeridoEsBoton =
    pct !== null &&
    descuentos.includes(pct) &&
    presentaciones.length > 0 &&
    presentaciones.every((x) => sugeridos.get(x.unidadVentaId) === precioConDescuento(x.regular, pct));
  const botones: BotonDescuento[] = descuentos.map((d) => ({ clave: String(d), pct: d, sugerido: sugeridoEsBoton && d === pct }));
  if (sugerencia && sugeridos.size > 0 && presentaciones.length > 0 && !sugeridoEsBoton) {
    const quitas = new Set(sugerencia.precios.map((x) => Math.round(descuentoDe(x.oferta, x.regular) / 100)));
    const propio = quitas.size === 1 ? [...quitas][0]! : null;
    const antesDe = propio === null ? 0 : botones.findIndex((b) => b.pct !== null && b.pct > propio);
    botones.splice(antesDe === -1 ? botones.length : antesDe, 0, { clave: 'sugerido', pct: propio, sugerido: true });
  }
  return { botones, sugeridoEsBoton };
}

// ---------------------------------------------------------------------------
// Lo que ahorra el cliente
// ---------------------------------------------------------------------------

/**
 * What a line saved the customer: the list's price without the offer, less what was charged, on what
 * was not brought back. Nothing on a line at no offer, or charged at the list's price or over it.
 */
export function ahorroDeLinea(l: Pick<VentaLinea, 'ofertaId' | 'precioRegular' | 'precioUnitario' | 'cantidad' | 'devuelta'>): Centavos {
  if (!l.ofertaId || l.precioRegular === undefined || l.precioRegular <= l.precioUnitario) return 0 as Centavos;
  const neta = subQty(l.cantidad, l.devuelta ?? ZERO_QTY);
  if (neta <= 0) return 0 as Centavos;
  return (lineTotal(l.precioRegular, neta) - lineTotal(l.precioUnitario, neta)) as Centavos;
}

/** What a sale's lines saved the customer, together. */
export function ahorroDe(lineas: readonly Parameters<typeof ahorroDeLinea>[0][]): Centavos {
  return lineas.reduce<number>((a, l) => a + ahorroDeLinea(l), 0) as Centavos;
}

// ---------------------------------------------------------------------------
// Lo que dio
// ---------------------------------------------------------------------------

/** How far back «Resultados» goes: the offers begun within these days. */
export const DIAS_DE_RESULTADOS = 90;
/** The weeks before an offer its pace is measured against: the four of «Por comprar». */
const DIAS_ANTES = VENTANA_OFERTAS;
/** After it ends, how long before a pace after it is said: less than a week says nothing. */
const DIAS_DESPUES_MINIMOS = 7;

/**
 * What an offer did (the owner's plan, 2026-11-03: «measuring whether each offer actually worked»):
 * how fast its product sold on its list before it and while it ran, what it sold at the offer and
 * brought in, what the customers did not pay, what it left over cost, what is left now and, of the
 * batches due by its last day, what is left and what was thrown out as expired since it began.
 * Quantities are in the product's base unit, in thousandths, as the books keep them.
 */
export interface ResultadoDeOferta {
  oferta: Oferta;
  estado: 'corre' | 'terminada';
  /** Its last day so far: today while it runs. */
  hasta: string;
  /** The days it has run, today included while it runs. */
  dias: number;
  /** What sold at the offer, net of what came back. */
  vendidas: Milli;
  /**
   * The product on the offer's list, a week: before it, out of offers, over its four weeks (fewer if
   * the shop had fewer, a week at least; zero with none); while it ran, everything, over the days it
   * ran; after it, out of offers, up to four weeks, once a week has gone by (null before, and while it
   * runs).
   */
  porSemanaAntes: Milli;
  porSemanaDurante: Milli;
  porSemanaDespues: Milli | null;
  /** How many times the pace before it, while it ran; null with nothing sold before. */
  veces: number | null;
  /** What its lines brought in, what the customers did not pay of the list's price, and what was left over cost. */
  ingreso: Centavos;
  descuento: Centavos;
  ganancia: Centavos;
  /** What there is of the product now, everywhere. */
  quedan: Milli;
  /**
   * The batches due from its first day to its last: what is left of them now, and what of the product
   * was taken out as expired since it began. Null when it was not for an expiring batch and there was
   * none.
   */
  lote: { quedan: Milli; botadas: Milli } | null;
}

export interface DatosResultados {
  ofertas: readonly Oferta[];
  /** From four weeks before the first of them on; voided ones are left out here. */
  ventas: readonly Venta[];
  salidas: readonly Salida[];
  lotes: readonly Lote[];
  existencias: readonly Existencia[];
  hoy: string;
}

/** A line of a sale as the results read it: its day, its list, how much of the product, and its money. */
interface Vendido {
  fecha: string;
  lista: string;
  base: number;
  ofertaId: string | null;
  ingreso: number;
  descuento: number;
  costo: number;
}

/**
 * So much over so many days, a week, in thousandths. Before an offer, over a week at least: a shop two
 * days old has no week's pace yet. While it runs, over its own days, the few it has run: the card says
 * how many.
 */
const aLaSemana = (base: number, dias: number, minimo = 1): Milli => (dias > 0 ? Math.round((base * 7) / Math.max(dias, minimo)) : 0) as Milli;

/**
 * Each offer that has run, or runs, as `ResultadoDeOferta` says: those still to start, and those
 * ended before they began, have nothing to say. One ended by hand ran until the day it was ended,
 * that day included: the till charged it that morning, until it knew (one ended the day it began
 * ran that day). The ones running first, the soonest to end first; then the ended ones, the latest
 * first.
 */
export function resultadosDeOfertas(d: DatosResultados): ResultadoDeOferta[] {
  const lineasDe = new Map<string, Vendido[]>();
  let primeraVenta: string | null = null;
  for (const v of d.ventas) {
    if (v.anulada) continue;
    if (primeraVenta === null || v.fecha < primeraVenta) primeraVenta = v.fecha;
    for (const l of v.lineas) {
      if (!l.productoId) continue;
      const neta = subQty(l.cantidad, l.devuelta ?? ZERO_QTY);
      if (neta <= 0) continue;
      const suyas = lineasDe.get(l.productoId) ?? [];
      suyas.push({
        fecha: v.fecha,
        lista: l.listaId ?? v.listaId,
        base: saleToBase(neta, l.factor),
        ofertaId: l.ofertaId ?? null,
        ingreso: lineTotal(l.precioUnitario, neta),
        descuento: ahorroDeLinea(l),
        // What was kept of the line costs its share of what the line cost.
        costo: Math.round((l.costoPosteado * neta) / l.cantidad),
      });
      lineasDe.set(l.productoId, suyas);
    }
  }
  const hay = new Map<string, number>();
  for (const e of d.existencias) if (e.cantidad > 0) hay.set(e.productoId, (hay.get(e.productoId) ?? 0) + e.cantidad);

  const resultados: ResultadoDeOferta[] = [];
  for (const o of d.ofertas) {
    const estado = estadoDeOferta(o, d.hoy);
    // Its last day as measured: the one it was ended on, when it was.
    const ultimo = o.terminadaEl !== null && o.terminadaEl <= o.hasta ? o.terminadaEl : o.hasta;
    if (estado === 'programada' || ultimo < o.desde) continue;
    const hasta = estado === 'corre' ? d.hoy : ultimo;
    const dias = diasEntre(o.desde, hasta) + 1;
    const suyas = (lineasDe.get(o.productoId) ?? []).filter((x) => x.lista === o.listaId);
    const entre = (desde: string, hastaEl: string, soloSinOferta: boolean) =>
      suyas.filter((x) => x.fecha >= desde && x.fecha <= hastaEl && (!soloSinOferta || x.ofertaId === null)).reduce((a, x) => a + x.base, 0);

    // Before: its four weeks, from the shop's first sale if that was later.
    const inicio = sumarDias(o.desde, -DIAS_ANTES);
    const desdeAntes = primeraVenta !== null && primeraVenta > inicio ? primeraVenta : inicio;
    const diasAntes = desdeAntes < o.desde ? diasEntre(desdeAntes, o.desde) : 0;
    const porSemanaAntes = aLaSemana(entre(desdeAntes, sumarDias(o.desde, -1), true), diasAntes, DIAS_MINIMOS);
    const porSemanaDurante = aLaSemana(entre(o.desde, hasta, false), dias);

    let porSemanaDespues: Milli | null = null;
    if (estado === 'terminada') {
      const despues = Math.min(diasEntre(ultimo, d.hoy), DIAS_ANTES);
      if (despues >= DIAS_DESPUES_MINIMOS) porSemanaDespues = aLaSemana(entre(sumarDias(ultimo, 1), sumarDias(ultimo, despues), true), despues);
    }

    const enOferta = (lineasDe.get(o.productoId) ?? []).filter((x) => x.ofertaId === o.id);
    const suma = (k: 'base' | 'ingreso' | 'descuento' | 'costo') => enOferta.reduce((a, x) => a + x[k], 0);

    const delLote = d.lotes.filter((l) => l.productoId === o.productoId && l.vencimiento >= o.desde && l.vencimiento <= o.hasta);
    const botadas = d.salidas
      .filter((x) => x.productoId === o.productoId && x.motivo === 'vencido' && x.fecha >= o.desde && x.fecha <= d.hoy)
      .reduce((a, x) => a + x.cantidadBase, 0);
    const lote =
      o.motivo === 'vence' || delLote.length > 0
        ? { quedan: delLote.reduce((a, l) => a + Math.max(0, l.cantidadDisponible), 0) as Milli, botadas: botadas as Milli }
        : null;

    resultados.push({
      oferta: o,
      estado,
      hasta,
      dias,
      vendidas: suma('base') as Milli,
      porSemanaAntes,
      porSemanaDurante,
      porSemanaDespues,
      veces: porSemanaAntes > 0 ? porSemanaDurante / porSemanaAntes : null,
      ingreso: suma('ingreso') as Centavos,
      descuento: suma('descuento') as Centavos,
      ganancia: (suma('ingreso') - suma('costo')) as Centavos,
      quedan: (hay.get(o.productoId) ?? 0) as Milli,
      lote,
    });
  }
  return resultados.sort(
    (a, b) =>
      (a.estado === 'corre' ? 0 : 1) - (b.estado === 'corre' ? 0 : 1) ||
      (a.estado === 'corre' ? ultimoDiaDeOferta(a.oferta).localeCompare(ultimoDiaDeOferta(b.oferta)) : b.hasta.localeCompare(a.hasta)) ||
      (a.oferta.id < b.oferta.id ? -1 : 1),
  );
}

// ---------------------------------------------------------------------------
// Por WhatsApp
// ---------------------------------------------------------------------------

/** «8 nov.», as the customers read a day: the shop's own Spanish whatever the phone's language. */
const diaCorto = (fecha: string) =>
  new Date(`${fecha}T12:00:00Z`).toLocaleDateString('es-NI', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * The offers running and to come on a list, for the shop's customers over WhatsApp (the owner's plan,
 * 2026-11-03: «a "this week's offers" WhatsApp message for clients»): the shop's name, each product
 * as the shop wrote it, each of its presentations at the offer with the list's price beside it, and
 * until when, or from when to when for one still to start; then the shop's address and phone. In
 * Spanish whatever the phone's language, as the receipt is: it is for the customers. Null with none.
 */
export function textoDeOfertas(d: {
  ofertas: readonly Oferta[];
  productos: readonly Producto[];
  unidades: readonly UnidadVenta[];
  precios: readonly { id: string; precio: Centavos }[];
  listaId: string;
  negocio: { nombre: string; direccion?: string | undefined; telefono?: string | undefined };
  hoy: string;
  /** A presentation as the customers read it: «Caja (12)». */
  presentacion: (u: UnidadVenta, unidadBase: string) => string;
}): string | null {
  const porId = new Map(d.productos.map((p) => [p.id, p]));
  const unidades = new Map(d.unidades.map((u) => [u.id, u]));
  const vigentes = d.ofertas
    .filter((o) => o.listaId === d.listaId && estadoDeOferta(o, d.hoy) !== 'terminada' && porId.get(o.productoId)?.activo)
    .sort((a, b) => a.desde.localeCompare(b.desde) || porId.get(a.productoId)!.nombre.localeCompare(porId.get(b.productoId)!.nombre, 'es'));
  if (vigentes.length === 0) return null;
  const l: string[] = [`*Ofertas de ${d.negocio.nombre.trim()}*`, ''];
  for (const o of vigentes) {
    const p = porId.get(o.productoId)!;
    const precios = o.precios.flatMap((x) => {
      const u = unidades.get(x.unidadVentaId);
      if (!u) return [];
      const regular = precioDe(d.precios, u.id, o.listaId);
      // A product sold in one presentation only says its price; with more, which one each is.
      const cual = o.precios.length > 1 ? `${d.presentacion(u, p.unidadBase)}: ` : '';
      // So many for fewer: what so many come to, at the list's price.
      if ('lleva' in x) return regular === null ? [] : [`${cual}${nombreDeLleva({ cantidad: x.lleva, paga: x.paga })}, lleva ${x.lleva} por ${formatMoney((regular * x.paga) as Centavos)}`];
      const antes = regular !== null && regular > x.precio ? ` (antes ${formatMoney(regular)})` : '';
      return [`${cual}${formatMoney(x.precio)}${antes}`];
    });
    if (precios.length === 0) continue;
    const ultimo = ultimoDiaDeOferta(o);
    const cuando = o.desde > d.hoy ? `del ${diaCorto(o.desde)} al ${diaCorto(ultimo)}` : `hasta el ${diaCorto(ultimo)}`;
    l.push(`• *${p.nombre}*: ${precios.join(' · ')}, ${cuando}`);
  }
  const contacto = [d.negocio.direccion, d.negocio.telefono].map((x) => x?.trim() ?? '').filter(Boolean);
  if (contacto.length > 0) l.push('', contacto.join(' · '));
  return l.join('\n');
}
